import React, { useEffect, useRef, useState } from "react";
import "../styles/Map.scss";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Tooltip from "./Tooltip";
import Legend from "./Legend";
import Attribution from "./Attribution";

mapboxgl.accessToken =
  "pk.eyJ1IjoibWFuc2hhciIsImEiOiJja2dqbGlvNnQwMTVxMnRtcGdoNDk4djdzIn0.tzsSfevkkBAfytZ7w9tWDg";

const styles = {
  width: "1200px",
  height: "650px",
  position: "absolute",
} as React.CSSProperties;

type MapProps = {
  data: Record<string, string>[];
  selectedOptions: Record<string, [string, string[]]>;
  colorStops: Record<string, []>;
};

const Map: React.FC<MapProps> = ({ data, colorStops, selectedOptions }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const [mapState, setMap] = useState<any | null>(null);

  // for ease of copy-pasta from mapbox studio
  var arr = [40.707, -73.889, 9.68];
  const [lat, setLat] = useState(arr[0]);
  const [lng, setLng] = useState(arr[1]);
  const [zoom, setZoom] = useState(arr[2]);
  const [tooltipData, setTooltipData] = useState<string[] | null>(null);
  const [pumaHovered, setPumaHovered] = useState<number | null>(null);
  var hoveredCdPuma: any = null;

  // to handle tooltip updates
  // to fire if data changes or the clicked puma changes
  useEffect(() => {
    var pumaData = data.filter((d) => +d.puma === pumaHovered)[0];

    if (pumaData) {
      setTooltipData([
        pumaData.cd_name,
        pumaData.count,
        pumaData.percent_final,
      ]);
    }
  }, [pumaHovered, data]);

  var isMounted = false;

  // construct mapbox fill expression
  function colorMap(map: any) {
    // set interpolate
    let fillExpression: (string | number | string[])[] = [
      "match",
      ["get", "puma"],
      0,
      "#d0cfd3",
    ];

    var findCut = (d: number) => {
      return colorStops.percent_final.reduce((cutIndex, cut, i) => {
        if (d > cut) {
          cutIndex = i;
        }
        return cutIndex;
      }, 0);
    };

    data.forEach((d) => {
      if (!+d.puma) return;
      fillExpression.push(+d.puma);

      fillExpression.push(colorStops.cols[findCut(+d.percent_final)]);
    });

    fillExpression.push("#d2d2d2");

    map.setPaintProperty("community-district", "fill-color", fillExpression);
    // map.setPaintProperty("community-district", "fill-outline-color", [
    //   "case",
    //   ["==", ["get", "puma"], 0],
    //   "#aaa",
    //   "#000",
    // ]);

    map.setPaintProperty("borough-boundaries-3ecmq3", "line-color", "black");
    map.setPaintProperty("borough-boundaries-3ecmq3", "line-width", 3);
    map.setPaintProperty("community-district-hover", "line-width", 2);
    map.setPaintProperty("community-district-hover", "line-color", "#9efafc");
    map.setPaintProperty("community-district-selected", "line-width", 2);

    map.setFilter("community-district-hover", ["all", ["in", "puma"]]);
    map.setFilter("community-district-selected", ["all", ["in", "puma"]]);

    // map.setFilter("community-district", ["!=", ["get", "puma"], 0]);

    if (pumaHovered) {
      map.setFilter("community-district-selected", [
        "==",
        ["get", "puma"],
        pumaHovered,
      ]);
    }
  }

  // update map
  // to fire whenever mapdata changes
  useEffect(() => {
    if (!mapContainer.current) return;

    const initializeMap = ({
      setMap,
      mapContainer,
    }: {
      setMap: any;
      mapContainer: any;
    }) => {
      isMounted = true;

      var map = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/manshar/ckhoyuwjk0ahi19o5unnhqrpg",
        center: [lng, lat],
        zoom: zoom,
        minZoom: 9,
        doubleClickZoom: false,
      });

      map.addControl(new mapboxgl.NavigationControl());

      map.on("load", () => {
        if (!isMounted) return;
        setMap(map);

        map.resize();

        map.addLayer({
          id: "bourough-labels",
          type: "symbol", //this can be line
          source: {
            type: "vector",
            url:
              "mapbox://cms-health-indicators.ckkidjhcc0fop27puhqtqqgnk-48h99", //vector tileset
          },
          "source-layer": "bourough-labels",
          paint: {
            "text-color": "black",
            "text-halo-color": "#fff",
            "text-halo-width": 2,
          },
          layout: {
            "text-size": 15,
            "text-field": "{name}",
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          },
        });

        map.moveLayer("borough-boundaries-3ecmq3");
        map.moveLayer("bourough-labels");

        if (pumaHovered) {
          // console.log(pumaHovered);
          map.setFilter("community-district-selected", [
            "==",
            ["get", "puma"],
            pumaHovered,
          ]);
        }

        if (data.length !== 0) colorMap(map);
      });

      console.log(map);

      map.on("mousemove", "community-district", function (e) {
        if (e.features && e.features.length >= 1 && e.features[0].properties) {
          setPumaHovered(+e.features[0].properties.puma);

          if (e.features[0].properties.puma !== 0) {
            map.setFilter("community-district-selected", [
              "==",
              ["get", "puma"],
              +e.features[0].properties.puma,
            ]);
          }
        } else {
          setPumaHovered(null);
          setTooltipData(null);
          map.setFilter("community-district-selected", ["all", ["in", "puma"]]);
        }
      });

      // map.on("mousemove", "community-district", function (e) {
      //   if (e.features && e.features.length > 0 && e.features[0].properties) {
      //     hoveredCdPuma = e.features[0].properties.puma;
      //     if (hoveredCdPuma !== 0) {
      //       map.setFilter("community-district-hover", [
      //         "==",
      //         ["get", "puma"],
      //         hoveredCdPuma,
      //       ]);
      //     }
      //   }
      // });
    };

    if (!mapState) {
      initializeMap({ setMap, mapContainer });
    } else {
      mapState.resize();
      if (data.length !== 0) colorMap(mapState);
    }

    return () => {
      isMounted = false;
    };
  }, [data]);

  return (
    <div id="map-wrapper">
      <Tooltip data={tooltipData} />
      <Legend colorStops={colorStops} selectedOptions={selectedOptions} />
      <div className="map-container">
        <div
          className="map"
          ref={(el) => (mapContainer.current = el)}
          style={styles}
        ></div>
      </div>
      <Attribution></Attribution>
    </div>
  );
};

export default Map;
